"use client";

import { useCallback, useEffect, useState } from "react";
import type { BannerImageComponent } from "../../types";
import { RuntimeImage, useRendererRuntime } from "../../runtime/RendererRuntime";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

/**
 * Calque d'agrandissement, repris du procédé de la galerie photo : même fond,
 * même bouton de fermeture, même touche Échap, défilement de la page bloqué.
 *
 * Une différence, voulue : un schéma se LIT. Le calque s'ouvre donc à la taille
 * de l'écran, et un second clic passe l'image à sa taille réelle dans un cadre
 * défilant, ce qui permet d'aller lire une case précise d'un organigramme.
 */
function BannerZoom({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const [pleineTaille, setPleineTaille] = useState(false);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", auClavier);
    const precedent = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", auClavier);
      document.body.style.overflow = precedent;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Image agrandie"}
      data-banner-zoom=""
      className="fixed inset-0 flex flex-col items-center justify-center p-4"
      style={{ zIndex: 120, backgroundColor: "rgba(2, 6, 23, 0.92)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div
        className={`max-h-[88vh] max-w-[94vw] ${pleineTaille ? "overflow-auto" : "overflow-hidden"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          onClick={() => setPleineTaille((v) => !v)}
          className={`rounded-md bg-white ${pleineTaille ? "max-w-none cursor-zoom-out" : "cursor-zoom-in object-contain"}`}
          style={pleineTaille ? undefined : { maxHeight: "88vh", maxWidth: "94vw" }}
        />
      </div>

      <p className="mt-4 text-xs text-white/60">
        {pleineTaille
          ? "Faites défiler pour parcourir. Cliquez sur l'image pour revenir à la vue d'ensemble."
          : "Cliquez sur l'image pour l'agrandir à sa taille réelle. Échap pour fermer."}
      </p>
    </div>
  );
}

/** Full-width banner image renderer with positioning options. */
export function BannerImageRenderer({ component }: { component: BannerImageComponent }) {
  const { src, alt, showLoadingCircle, zoom } = component.content;
  const { assetResolver } = useRendererRuntime();
  const { src: imageSrc, options } = resolveImageValue(src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const [agrandie, setAgrandie] = useState(false);
  // Sans réglage éditorial, une photo portrait occuperait toute la hauteur de
  // l'écran : la bannière est plafonnée par défaut, l'override restant maître.
  const hasHeightOverride = overrideProps.className?.includes("image-override");
  // `fit: "contain"` = l'image doit être vue EN ENTIER, quelle que soit la
  // résolution. Un plafond de hauteur avec `object-cover` la recadrerait : pour
  // un schéma, un organigramme ou un texte de loi, ce qui est coupé est perdu.
  // On rend alors l'image à son rapport naturel (largeur pleine, hauteur libre).
  const entiere = options?.fit === "contain" && !hasHeightOverride;
  // L'agrandissement est DEMANDÉ PAR LE CONTENU (`zoom: true`), jamais par
  // défaut : une photo d'illustration n'a rien à gagner à s'ouvrir en grand,
  // seul un visuel qui se lit le mérite.
  const agrandissable = zoom === true || zoom === "true";

  const image = (
    <RuntimeImage
      src={imageSrc}
      alt={alt}
      showLoadingCircle={showLoadingCircle === true || showLoadingCircle === "true"}
      className={mergeClassNames(
        "w-full rounded-md ring-1 ring-black/10",
        entiere ? "h-auto" : undefined,
        hasHeightOverride || entiere ? undefined : "max-h-[280px] md:max-h-[360px] xl:max-h-[440px] object-cover",
        overrideProps.className
      )}
      style={mergeStyles(objectPositionStyle, overrideProps.style)}
      priority
    />
  );

  const ouvrir = useCallback(() => setAgrandie(true), []);
  const fermer = useCallback(() => setAgrandie(false), []);

  if (!agrandissable) {
    return <section>{image}</section>;
  }

  return (
    <section>
      <div className="group relative">
        <button
          type="button"
          onClick={ouvrir}
          aria-label={`Agrandir : ${alt || "l'image"}`}
          className="block w-full cursor-zoom-in"
        >
          {image}
          <span
            aria-hidden="true"
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-slate-950/55 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3M11 8v6M8 11h6" />
            </svg>
          </span>
        </button>
      </div>
      {agrandie ? (
        <BannerZoom
          src={assetResolver ? assetResolver(imageSrc) : imageSrc}
          alt={alt}
          onClose={fermer}
        />
      ) : null}
    </section>
  );
}
